import React, { useMemo, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const TOOLBAR_OPTIONS = [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
];

const RichTextEditor = forwardRef(function RichTextEditor(
    { value, onChange, placeholder, readOnly = false, onAtTrigger, onAtDismiss },
    ref
) {
    const quillRef = useRef(null);
    const atTrackingRef = useRef({ active: false, startIndex: -1 });

    const modules = useMemo(() => ({
        toolbar: readOnly ? false : TOOLBAR_OPTIONS,
    }), [readOnly]);

    const formats = useMemo(() => [
        "bold", "italic", "underline", "list", "link",
    ], []);

    useImperativeHandle(ref, () => ({
        insertLink(text, url) {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;
            const { active, startIndex } = atTrackingRef.current;
            if (active && startIndex >= 0) {
                const currentPos = editor.getSelection()?.index ?? editor.getLength();
                const deleteLen = currentPos - startIndex;
                editor.deleteText(startIndex, deleteLen);
                editor.insertText(startIndex, text, "link", url);
                editor.insertText(startIndex + text.length, " ");
                editor.setSelection(startIndex + text.length + 1);
            }
            atTrackingRef.current = { active: false, startIndex: -1 };
        },
        focus() {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;
            editor.focus();
            const len = editor.getLength();
            editor.setSelection(Math.max(0, len - 1));
        },
        insertLinkAtCursor(text, url) {
            const editor = quillRef.current?.getEditor();
            if (!editor || !text || !url) return;
            editor.focus();
            let sel = editor.getSelection();
            if (!sel) {
                const len = Math.max(0, editor.getLength() - 1);
                editor.setSelection(len, 0);
                sel = editor.getSelection() || { index: len, length: 0 };
            }
            const index = sel.index;
            if (sel.length > 0) editor.deleteText(index, sel.length);
            editor.insertText(index, text, "link", url);
            editor.insertText(index + text.length, " ");
            editor.setSelection(index + text.length + 1);
        },
    }), []);

    const handleTextChange = useCallback(() => {
        if (!onAtTrigger) return;
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const sel = editor.getSelection();
        if (!sel) return;

        const textBefore = editor.getText(0, sel.index);
        const lastAt = textBefore.lastIndexOf("@");

        if (lastAt >= 0) {
            const afterAt = textBefore.slice(lastAt + 1);
            // Allow spaces in search — only dismiss if there's a newline after @
            if (!afterAt.includes("\n")) {
                atTrackingRef.current = { active: true, startIndex: lastAt };
                // Get cursor bounds for dropdown positioning
                const bounds = editor.getBounds(sel.index);
                onAtTrigger(afterAt, bounds);
                return;
            }
        }
        if (atTrackingRef.current.active) {
            atTrackingRef.current = { active: false, startIndex: -1 };
            onAtDismiss?.();
        }
    }, [onAtTrigger, onAtDismiss]);

    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor || !onAtTrigger) return;
        editor.on("text-change", handleTextChange);
        return () => editor.off("text-change", handleTextChange);
    }, [handleTextChange, onAtTrigger]);

    return (
        <div className="rich-text-editor">
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                readOnly={readOnly}
                modules={modules}
                formats={formats}
            />
            <style>{`
                .rich-text-editor .ql-container { min-height: 100px; font-size: 14px; }
                .rich-text-editor .ql-editor { min-height: 100px; }
                .rich-text-editor .ql-editor p { margin-bottom: 0.5em; }
                .rich-text-editor .ql-toolbar { border-radius: 8px 8px 0 0; }
                .rich-text-editor .ql-container { border-radius: 0 0 8px 8px; }
            `}</style>
        </div>
    );
});

export default RichTextEditor;
